/*eslint-disable*/
import React from "react";
import {
  useTable,
  useFilters,
  useAsyncDebounce,
  useSortBy,
  usePagination,
} from "react-table";
import classnames from "classnames";
// A great library for fuzzy filtering/sorting items
import matchSorter from "match-sorter";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InputLabel from "@material-ui/core/InputLabel";
import Switch from "@material-ui/core/Switch";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
// core components
import CustomInput from "components/CustomInput/CustomInput";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Button from "components/CustomButtons/Button";
import BlueTypo from "components/Typography/Info";
import styles from "assets/jss/elite-feats-cms/customSelectStyleWarning";
import { useTranslation } from "react-i18next";
import GroupCardBase from "../Dashboard/GroupCard";
const GroupCard = GroupCardBase as React.ComponentType<{
  group: unknown;
  index?: number;
  onToggle: (groupName: string) => void;
  isChecked?: unknown;
  [key: string]: unknown;
}>;

const newStyles = {
  ...styles,

  formControlMargins: {
    margin: "3px 0 !important",
  },
  gridContainer: {
    justifyContent: "center",
  },
  tbcell: {
    display: "flex",
    alignItems: "center",
    paddingTop: 7,
    paddingBottom: 7,
    width: 50,
  },
};

const useStyles = makeStyles(newStyles);

function fuzzyTextFilterFn(
  rows: import("react-table").Row[],
  id: string,
  filterValue: string
) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val: unknown) => !val;

// Our table component
interface TableProps {
  columns: import("react-table").Column[];
  data: Record<string, unknown>[];
  reload2?: unknown;
  groupTour?: unknown;
  isMobile?: boolean;
  setGroupEdit?: (v: unknown) => void;
  setOpenEdit?: (v: boolean) => void;
  setSelectedGroup?: (v: unknown) => void;
  setShouldOpen?: (v: boolean) => void;
  setOpenGroupMail?: (v: boolean) => void;
  setGroupToMail?: (v: unknown) => void;
  groups?: Array<{ name?: string; [key: string]: unknown }>;
  setReloadGroupsTable?: (v: unknown) => void;
  reloadClients?: unknown;
}

function Table({
  columns,
  data,
  reload2,
  groupTour,
  isMobile,
  setGroupEdit,
  setOpenEdit,
  setSelectedGroup,
  setShouldOpen,
  setOpenGroupMail,
  setGroupToMail,
  groups,
  setReloadGroupsTable,
}: TableProps): React.ReactElement {
  const { t } = useTranslation();
  const [reload, setReload] = React.useState(0);

  const [numberOfRows, setNumberOfRows] = React.useState(10);
  const [pageSelect, handlePageSelect] = React.useState(0);

  function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter },
  }: {
    column: {
      filterValue: string;
      preFilteredRows: import("react-table").Row[];
      setFilter: (v: unknown) => void;
    };
  }) {
    const count = preFilteredRows.length;

    return (
      <CustomInput
        formControlProps={{
          fullWidth: true,
        }}
        inputProps={{
          value: filterValue || "",
          onChange: (e) => {
            setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
          },
          placeholder: t("clients:searchPlaceholder", { count }),
        }}
      />
    );
  }

  const classes = useStyles() as Record<string, string>;
  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (!firstRender.current) {
      if (groupTour === -5) {
        setNumberOfRows(5);
        setPageSize(5);
      } else if (groupTour === -10) {
        /*    setNumberOfRows(10);
        setPageSize(10); */
      }
    } else {
      firstRender.current = false;
    }
  }, [groupTour]);

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (
      rows: import("react-table").Row[],
      id: string,
      filterValue: string
    ) => {
        return rows.filter((row: import("react-table").Row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    visibleColumns,
    nextPage,
    pageOptions,
    pageCount,
    previousPage,
    canPreviousPage,
    canNextPage,
    setPageSize,
    gotoPage,
    setAllFilters,
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: { pageSize: 10, pageIndex: 0 },
    },
    useFilters, // useFilters!
    useSortBy,
    usePagination
  );

  // We don't want to render all of the rows for this example, so cap
  // it for this use case
  // const firstPageRows = rows.slice(0, 10);
  let pageSelectData = Array.apply(
    null,
    Array(pageOptions.length)
  ).map(function () {});
  let numberOfRowsData = [5, 10, 20, 25, 50, 100];

  // Mobile card view logic
  const [selectedGroups, setSelectedGroups] = React.useState(
    JSON.parse(localStorage.getItem("selected_groups") as string) || []
  );

  React.useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("selected_groups") as string) || [];
    setSelectedGroups(stored);
  }, [reload2]);

  const handleToggle = (groupName: string) => {
    let currentSelected = JSON.parse(localStorage.getItem("selected_groups") as string) || [];

    // Find the full group object
    const fullGroup = groups?.find((g: { name?: string; [key: string]: unknown }) => g.name === groupName);
    if (!fullGroup) return;

    // Check if group is already selected (by name)
    const selectedIndex = currentSelected.findIndex((g: { name?: string; [key: string]: unknown }) => g.name === groupName);

    if (selectedIndex !== -1) {
      // Remove it
      currentSelected.splice(selectedIndex, 1);
    } else {
      // Add the full group object
      currentSelected.push(fullGroup);
    }

    localStorage.setItem("selected_groups", JSON.stringify(currentSelected));
    setSelectedGroups(currentSelected);
    setReload(Math.random());

    // Trigger parent update to show/hide actions panel
    if (setReloadGroupsTable) {
      setReloadGroupsTable!(Math.random());
    }
  };

  // Mobile card view rendering
  if (isMobile) {
    // Get filtered and sorted data
    const filteredData = page.map((row) => {
      prepareRow(row);
      return row.original;
    });

    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <CustomInput
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              value: state.filters[0]?.value || "",
              onChange: (e) => {
                setAllFilters([{ id: "name", value: e.target.value }]);
              },
              placeholder: t("groups:searchGroups"),
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          {filteredData.map((group, index) => {
            // Find the full group object with all data
            const fullGroup = groups?.find((g: { name?: string; [key: string]: unknown }) => g.name === group.name);

            return (
              <GroupCard
                key={group.name as React.Key}
                group={group}
                index={index}
                onToggle={handleToggle}
                onEdit={(g: Record<string, unknown>) => {
                  if (fullGroup) {
                    setGroupEdit!({
                      name: fullGroup.name,
                      description: fullGroup.description || ""
                    });
                    setOpenEdit!(true);
                  }
                }}
                onMembers={(g: Record<string, unknown>) => {
                  if (fullGroup) {
                    setSelectedGroup!(fullGroup);
                    setShouldOpen!(true);
                  }
                }}
                onEmail={(g: Record<string, unknown>) => {
                  if (fullGroup) {
                    setGroupToMail!(fullGroup);
                    setOpenGroupMail!(true);
                  }
                }}
                isChecked={selectedGroups.some((g: { name?: string; [key: string]: unknown }) => g.name === group.name)}
              />
            );
          })}
        </div>

        <GridContainer style={{ marginTop: 16 }} className={classes.gridContainer}>
          <GridItem xs={6} style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <Typography variant="body2">
              {t("clients:page")} {state.pageIndex + 1} {t("clients:of")} {pageOptions.length}
            </Typography>
          </GridItem>
          <GridItem xs={6} style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              color="info"
              size="sm"
              disabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              {t("clients:previous")}
            </Button>
            <Button
              color="info"
              size="sm"
              disabled={!canNextPage}
              onClick={() => nextPage()}
            >
              {t("clients:next")}
            </Button>
          </GridItem>
        </GridContainer>
      </>
    );
  }

  // Desktop table view
  return (
    <>
      <div className="ReactTable -striped -highlight">
        <table {...getTableProps()} className="rt-table">
          <thead className="rt-thead -header">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr">
                {headerGroup.headers.map((column, key) => {
                  return (
                    <th
                      {...column.getHeaderProps()}
                      className={
                        column.id !== "select"
                          ? classnames("rt-th rt-resizable-header")
                          : classes.tbcell
                      }
                    >
                      <div
                        {...column.getSortByToggleProps()}
                        className={
                          column.id !== "select"
                            ? classnames({
                                "-cursor-pointer":
                                  headerGroup.headers.length - 1 !== key,

                                "-sort-asc":
                                  column.isSorted && !column.isSortedDesc,

                                "-sort-desc":
                                  column.isSorted && column.isSortedDesc,
                              })
                            : classes.tbcell
                        }
                        style={{
                          display: "flex",
                          justifyContent:
                            column.id === "actions" ? "center" : undefined,
                        }}
                      >
                        <Typography
                          style={{
                            display: "flex",
                            alignItems: "center",

                            fontSize: 18,
                            fontWeight: "bold",
                          }}
                        >
                          {" "}
                          {column.render("Header")}
                        </Typography>
                      </div>{" "}
                      {/* Render the columns filter UI */}
                      <div
                        style={{
                          marginTop: column.id === "actions" ? 0 : -30,
                          maxWidth: 150,
                          display: "flex",
                        }}
                      >
                        {headerGroup.headers.length - 1 === key
                          ? null
                          : column.id === "select" || column.id === "actions"
                          ? null
                          : column.canFilter
                          ? column.render("Filter")
                          : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody
            {...getTableBodyProps()}
            className="rt-tbody"
            style={{
              marginTop: -15,
              maxHeight: 400,
              border: "1px solid #e9e9e9",
              borderRadius: 5,
            }}
          >
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  className={classnames(
                    "rt-tr",
                    { " -odd": i % 2 === 0 },
                    { " -even": i % 2 === 1 },
                    i === 0 ? "latest-group" : null
                  )}
                  style={{
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                >
                  {" "}
                  {row.cells.map((cell) => {
                    return (
                      <td
                        style={{
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        {...cell.getCellProps()}
                        className={
                          cell.column.id !== "select" ? "rt-td" : classes.tbcell
                        }
                      >
                        {cell.render("Cell").props.cell.value === "-" ||
                        typeof cell.render("Cell").props.cell.value ===
                          "number" ? (
                          <Typography
                            style={{
                              marginLeft: 40,
                              paddingLeft: 5,
                              fontWeight: 500,
                              color: "#006EB3",
                            }}
                          >
                            {" "}
                            {cell.render("Cell")}{" "}
                          </Typography>
                        ) : (
                          <Typography
                            style={{
                              fontWeight: 400,
                              color: "#006EB3",
                            }}
                          >
                            {" "}
                            {cell.render("Cell")}{" "}
                          </Typography>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="pagination-top">
          <div className="-pagination" style={{ marginTop: 5 }}>
            <div className="-previous">
              {canPreviousPage ? (
                <Button
                  size="sm"
                  color="info"
                  onClick={() => {
                    previousPage();
                    handlePageSelect(pageSelect - 1);
                  }}
                  disabled={!canPreviousPage}
                >
                  {t("clients:pagination.previous")}
                </Button>
              ) : (
                false
              )}
            </div>
            <div className="-center">
              <GridContainer className={classes.gridContainer}>
                <GridItem xs={12} sm={6} md={4}>
                  <FormControl
                    fullWidth
                    className={
                      classes.selectFormControl +
                      " " +
                      classes.formControlMargins
                    }
                  >
                    <Select
                      MenuProps={{
                        className: classes.selectMenu,
                      }}
                      classes={{
                        select: classes.select,
                      }}
                      value={pageSelect}
                      onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                        gotoPage(event.target.value as number);
                        handlePageSelect(event.target.value as number);
                      }}
                      inputProps={{
                        name: "pageSelect",
                        id: "page-select",
                      }}
                    >
                      {pageSelectData.map((prop, key) => {
                        return (
                          <MenuItem
                            key={key}
                            classes={{
                              root: classes.selectMenuItem,
                              selected: classes.selectMenuItemSelected,
                            }}
                            value={key}
                          >
                            {t("clients:pagination.page", { number: key + 1 })}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={6} md={4}>
                  <FormControl
                    fullWidth
                    className={
                      classes.selectFormControl +
                      " " +
                      classes.formControlMargins
                    }
                  >
                    <Select
                      MenuProps={{
                        className: classes.selectMenu,
                      }}
                      classes={{
                        select: classes.select,
                      }}
                      value={numberOfRows}
                      onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                        setPageSize(event.target.value as number);
                        setNumberOfRows(event.target.value as number);
                      }}
                      inputProps={{
                        name: "numberOfRows",
                        id: "number-of-rows",
                      }}
                    >
                      {numberOfRowsData.map((prop) => {
                        return (
                          <MenuItem
                            key={prop}
                            classes={{
                              root: classes.selectMenuItem,
                              selected: classes.selectMenuItemSelected,
                            }}
                            value={prop}
                          >
                            {t("clients:pagination.rows", { count: prop })}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </GridItem>
              </GridContainer>
            </div>

            <div className="-next">
              {canNextPage ? (
                <Button
                  size="sm"
                  color="info"
                  onClick={() => {
                    nextPage();
                    handlePageSelect(pageSelect + 1);
                  }}
                  disabled={!canNextPage}
                >
                  {t("clients:pagination.nextPage")}
                </Button>
              ) : (
                false
              )}
            </div>
          </div>
        </div>
        <div className="pagination-bottom"></div>
      </div>
    </>
  );
}

// Define a custom filter filter function!
function filterGreaterThan(
  rows: import("react-table").Row[],
  id: string,
  filterValue: number
) {
  return rows.filter((row: import("react-table").Row) => {
    const rowValue = row.values[id];
    return (rowValue as number) >= filterValue;
  });
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = (val: unknown) => typeof val !== "number";

export default Table;
